import {type ElementType, type HTMLAttributes} from 'react';

import {cn} from '@/lib/utils';

export type LEVEL =
  | 'D1'
  | 'D2'
  | 'D3'
  | 'D4'
  | 'H1'
  | 'H2'
  | 'H3'
  | 'H4'
  | 'H5'
  | 'S1'
  | 'S2'
  | 'B1'
  | 'B2'
  | 'B3'
  | 'B4'
  | 'C1'
  | 'C2'
  | 'C3'
  | 'C4'
  | 'LABEL'
  | 'BTN_GIANT'
  | 'BTN_LARGE'
  | 'BTN_MEDIUM'
  | 'BTN_SMALL'
  | 'BTN_TINY';

type Breakpoint = 'sm' | 'md' | 'lg' | 'xl' | '2xl';

type ResponsiveLevel = Partial<Record<Breakpoint, LEVEL>>;

type LevelConfig = {
  tag: ElementType;
  classes: string;
};

const LEVEL_CONFIG: Record<LEVEL, LevelConfig> = {
  // Display — serif, used for major headlines and taglines only
  D1: {
    tag: 'h1',
    classes:
      'md:text-9xl text-8xl tracking-[-0.03em] leading-[1.19] font-black',
  },
  D2: {
    tag: 'h2',
    classes:
      'md:text-8xl text-7xl tracking-[-0.03em] leading-[1.19] font-black',
  },
  D3: {
    tag: 'h3',
    classes:
      'md:text-[4rem] text-[3rem] tracking-[-0.03em] leading-[1.19] font-black',
  },
  D4: {
    tag: 'h4',
    classes:
      'md:text-5xl text-4xl tracking-[-0.03em] leading-[1.19] font-black',
  },

  // Headings
  H1: {tag: 'h1', classes: 'text-5xl font-bold leading-[1.2083]'},
  H2: {
    tag: 'h2',
    classes: 'text-[2.5rem] font-bold leading-[1.2]',
  },
  H3: {
    tag: 'h3',
    classes: 'text-[2rem] font-medium leading-[1.1875]',
  },
  H4: {
    tag: 'h4',
    classes: 'text-[1.75rem] font-medium leading-[1.2143]',
  },
  H5: {
    tag: 'h5',
    classes: 'text-2xl font-medium leading-[1.1667]',
  },

  // Subheadings
  S1: {tag: 'p', classes: 'text-lg font-medium leading-[1.5556]'},
  S2: {tag: 'p', classes: 'text-base font-medium leading-[1.5]'},

  // Body
  B1: {tag: 'p', classes: 'text-base font-normal leading-[1.35]'},
  B2: {tag: 'p', classes: 'text-base font-medium leading-[1.35]'},
  B3: {tag: 'p', classes: 'text-sm font-normal leading-[1.4286]'},
  B4: {tag: 'p', classes: 'text-sm font-medium leading-[1.4286]'},

  // Captions
  C1: {
    tag: 'span',
    classes: 'text-xs font-normal leading-[1.3333]',
  },
  C2: {
    tag: 'span',
    classes: 'text-xs font-medium leading-[1.3333]',
  },
  C3: {
    tag: 'span',
    classes: 'text-[0.625rem] font-normal leading-[1.6]',
  },
  C4: {
    tag: 'span',
    classes: 'text-[0.625rem] font-medium leading-[1.6]',
  },

  // Label
  LABEL: {
    tag: 'label',
    classes: 'text-xs font-medium leading-[1.3333]',
  },

  // Button text — standalone text styles, not interactive elements
  BTN_GIANT: {
    tag: 'span',
    classes: 'text-lg font-bold leading-[1.3333]',
  },
  BTN_LARGE: {
    tag: 'span',
    classes: 'text-base font-bold leading-[1.25]',
  },
  BTN_MEDIUM: {
    tag: 'span',
    classes: 'text-sm font-medium leading-[1.1429]',
  },
  BTN_SMALL: {
    tag: 'span',
    classes: 'text-xs font-medium leading-[1.3333]',
  },
  BTN_TINY: {
    tag: 'span',
    classes: 'text-[0.625rem] font-medium leading-[1.2]',
  },
};

const BREAKPOINT_ORDER: Breakpoint[] = ['sm', 'md', 'lg', 'xl', '2xl'];

/**
 * Prefixes every space-separated Tailwind class in `classes` with the given
 * breakpoint, e.g. `text-4xl font-semibold` → `lg:text-4xl lg:font-semibold`.
 */
function prefixClasses(breakpoint: Breakpoint, classes: string): string {
  return classes
    .trim()
    .split(/\s+/)
    .map((cls) => `${breakpoint}:${cls}`)
    .join(' ');
}

interface TypographyProps extends HTMLAttributes<HTMLElement> {
  /**
   * The typographic level to render at. Pass a plain `LEVEL` string for a
   * fixed style, or a `ResponsiveLevel` object to vary styles across
   * breakpoints. When using the object form, the smallest breakpoint key
   * provided acts as the base (mobile-first), and the HTML tag is derived
   * from it.
   *
   * @example
   * // Fixed
   * <Typography level="H1">Hello</Typography>
   *
   * @example
   * // Responsive — small screens get B1, large get H2
   * <Typography level={{ sm: "B1", lg: "H2" }}>Hello</Typography>
   */
  level: LEVEL | ResponsiveLevel;
}

/**
 * Renders text at a given typographic level using the appropriate semantic
 * HTML tag and design-system Tailwind classes. Consumer-provided `className`
 * values are merged last and will override the defaults.
 *
 * Pass a responsive object to `level` to swap visual styles at specific
 * breakpoints while keeping the base level's semantic tag unchanged.
 */
export const Typography = ({
  level,
  className,
  children,
  ...props
}: TypographyProps) => {
  if (typeof level === 'string') {
    const {tag: Tag, classes} = LEVEL_CONFIG[level];
    return (
      <Tag className={cn('text-neutral-900', classes, className)} {...props}>
        {children}
      </Tag>
    );
  }

  const sortedEntries = BREAKPOINT_ORDER.flatMap((bp) =>
    bp in level ? [[bp, level[bp]] as [Breakpoint, LEVEL]] : []
  );

  const [[, baseLevel], ...remainingEntries] = sortedEntries;
  const {tag: Tag, classes} = LEVEL_CONFIG[baseLevel];

  const responsiveClasses = remainingEntries
    .map(([breakpoint, responsiveLevel]) =>
      prefixClasses(breakpoint, LEVEL_CONFIG[responsiveLevel].classes)
    )
    .join(' ');

  return (
    <Tag
      className={cn('text-neutral-900', classes, responsiveClasses, className)}
      {...props}
    >
      {children}
    </Tag>
  );
};
